import { useEffect, useRef, useState } from 'react';
import PostalMime, { type Email } from 'postal-mime';
import DOMPurify from 'dompurify';
import { Download, Loader2, Mail, Paperclip } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ParsedAttachment {
  filename: string;
  mimeType: string;
  url: string;
  size: number;
}

interface ParsedEmail {
  from: string;
  to: string;
  cc: string;
  subject: string;
  date: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: ParsedAttachment[];
}

function formatAddress(a?: { name?: string; address?: string } | null): string {
  if (!a) return '';
  if (a.name && a.address) return `${a.name} <${a.address}>`;
  return a.address ?? a.name ?? '';
}

function formatAddressList(list?: { name?: string; address?: string }[]): string {
  return (list ?? []).map(formatAddress).filter(Boolean).join(', ');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// DOMPurify config: keep the email faithful (remote images load per product decision)
// but strip scripts/handlers. Allow blob:/data: so inline (CID) images resolve.
const SANITIZE_CONFIG: Parameters<typeof DOMPurify.sanitize>[1] = {
  ADD_ATTR: ['target'],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|blob|data|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Lightweight in-app .eml viewer. Fetches the raw message bytes from the
 * attachments endpoint, parses the MIME with postal-mime, rewrites inline
 * CID images to blob URLs, sanitises the HTML, and lists nested attachments.
 */
export function EmlViewer({
  attachmentId,
  label,
  trigger,
}: {
  attachmentId: number;
  label: string;
  /** Custom trigger element. Defaults to a link styled for the Files list. */
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<ParsedEmail | null>(null);
  // Object URLs we create (inline images + attachment downloads) — revoked on unmount.
  const objectUrls = useRef<string[]>([]);
  // Guard so we fetch/parse once per attachment, not on every open toggle.
  const loadedRef = useRef(false);

  useEffect(() => () => {
    objectUrls.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrls.current = [];
  }, []);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/attachments/${attachmentId}/data`);
        if (!res.ok) throw new Error(`Failed to load email (${res.status})`);
        const buf = await res.arrayBuffer();
        const parsed: Email = await new PostalMime().parse(buf);
        if (cancelled) return;

        // Map inline (CID) images to blob URLs and collect real attachments.
        const cidMap = new Map<string, string>();
        const attachments: ParsedAttachment[] = [];
        for (const att of parsed.attachments ?? []) {
          const part: BlobPart = typeof att.content === 'string' ? att.content : new Uint8Array(att.content);
          const size = typeof att.content === 'string' ? att.content.length : att.content.byteLength;
          const blobUrl = URL.createObjectURL(
            new Blob([part], { type: att.mimeType || 'application/octet-stream' }),
          );
          objectUrls.current.push(blobUrl);
          const isInline = att.related === true || (!!att.contentId && att.disposition !== 'attachment');
          if (att.contentId) {
            const cid = att.contentId.replace(/[<>]/g, '');
            cidMap.set(cid, blobUrl);
          }
          if (att.filename) cidMap.set(att.filename, blobUrl);
          if (!isInline) {
            attachments.push({
              filename: att.filename || 'attachment',
              mimeType: att.mimeType || 'application/octet-stream',
              url: blobUrl,
              size,
            });
          }
        }

        let bodyHtml = parsed.html ?? null;
        if (bodyHtml) {
          bodyHtml = bodyHtml.replace(/cid:([^"')\s>]+)/gi, (m, ref: string) => {
            const key = ref.replace(/[<>]/g, '');
            return cidMap.get(key) ?? cidMap.get(decodeURIComponent(key)) ?? m;
          });
        }

        loadedRef.current = true;
        setEmail({
          from: formatAddress(parsed.from),
          to: formatAddressList(parsed.to),
          cc: formatAddressList(parsed.cc),
          subject: parsed.subject ?? '(no subject)',
          date: parsed.date ?? '',
          bodyHtml,
          bodyText: parsed.text ?? null,
          attachments,
        });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not parse email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, attachmentId]);

  const cleanHtml = email?.bodyHtml ? DOMPurify.sanitize(email.bodyHtml, SANITIZE_CONFIG) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className="flex-1 truncate text-left text-sm font-medium text-brand-strong hover:underline dark:text-brand"
          >
            {label}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="flex max-h-[88vh] w-[min(900px,94vw)] max-w-none flex-col gap-0 p-0">
        <DialogHeader className="shrink-0 border-b border-divider">
          <DialogTitle className="flex items-center gap-2 pr-6">
            <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{email?.subject ?? label}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-6 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading email…
            </div>
          )}

          {error && (
            <div className="space-y-3 px-6 py-8">
              <p className="text-sm text-destructive">{error}</p>
              <a
                href={`/api/attachments/${attachmentId}/data?download=1`}
                className="inline-flex items-center gap-1.5 text-sm text-brand-strong hover:underline dark:text-brand"
              >
                <Download className="h-3.5 w-3.5" /> Download the original instead
              </a>
            </div>
          )}

          {email && !loading && !error && (
            <div className="space-y-4 px-6 py-4">
              {/* Header fields */}
              <dl className="space-y-1 rounded-md bg-muted/40 px-3 py-2.5 text-xs">
                <HeaderRow label="From" value={email.from} />
                <HeaderRow label="To" value={email.to} />
                {email.cc && <HeaderRow label="Cc" value={email.cc} />}
                {email.date && (
                  <HeaderRow label="Date" value={new Date(email.date).toLocaleString()} />
                )}
              </dl>

              {/* Nested attachments */}
              {email.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {email.attachments.map((att, i) => (
                    <a
                      key={i}
                      href={att.url}
                      download={att.filename}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-muted"
                    >
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="max-w-[220px] truncate">{att.filename}</span>
                      <span className="text-muted-foreground">{formatBytes(att.size)}</span>
                    </a>
                  ))}
                </div>
              )}

              {/* Body — rendered on a white "paper" surface in both themes.
                  HTML emails carry their own colours (often black text) and
                  assume a light background, so against the dark dialog they'd be
                  unreadable. Forcing white bg + dark text matches Gmail/Outlook. */}
              {cleanHtml ? (
                <div
                  className="eml-body rounded-md bg-white p-4 text-sm leading-relaxed text-neutral-900 [&_a]:text-brand-strong [&_img]:max-w-full [&_table]:max-w-full"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: cleanHtml }}
                />
              ) : email.bodyText ? (
                <pre className="whitespace-pre-wrap break-words rounded-md bg-white p-4 font-sans text-sm leading-relaxed text-neutral-900">
                  {email.bodyText}
                </pre>
              ) : (
                <p className="text-sm italic text-muted-foreground">This email has no readable body.</p>
              )}
            </div>
          )}
        </div>

        {email && (
          <div className="flex shrink-0 justify-end border-t border-divider px-6 py-3">
            <Button asChild variant="outline" size="sm">
              <a href={`/api/attachments/${attachmentId}/data?download=1`}>
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download .eml
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HeaderRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-12 shrink-0 font-medium text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-foreground">{value}</dd>
    </div>
  );
}
